import { Fragment, useMemo, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { CheckCircleRounded, PaidOutlined, WarningAmberRounded } from "@mui/icons-material";
import dayjs from "dayjs";
import CoordinatorAvatars from "../_components/CoordinatorAvatars";
import HighlightStar from "../_components/HighlightStar";
import {
  barLook,
  clientLabel,
  dateRangeLabel,
  LIFECYCLE_LABELS,
  NEON,
  placeLabel,
  SEVERITY_COLORS,
  THEME,
} from "./boardStyles";
import type { BoardShow, ShowMoney } from "./boardTypes";

/** Past bar text: "Closed", or what is still open ("Client 60% · Crew 3/10"). */
function moneyBarText(money: ShowMoney, width: number): string {
  if (money.finalized) return "Closed";
  if (money.client.waived && money.client.due === 0) return "Comped · review costs";
  const pct = `${money.client.percent ?? 0}%`;
  if (width < 100) return money.client.finalInvoiceSent ? pct : "No inv.";
  const client = money.client.finalInvoiceSent ? `Client ${pct}` : "No invoice";
  const crew = money.crew.people ? `Crew ${money.crew.paidPeople}/${money.crew.people}` : "";
  return width >= 150 && crew ? `${client} · ${crew}` : client;
}

const LABEL_WIDTH = 300;
const LABEL_WIDTH_SMALL = 180;
const ROW_HEIGHT = 48;
const HEADER_HEIGHT = 58;

type Props = {
  shows: BoardShow[];
  /** Visible window, inclusive, YYYY-MM-DD. */
  from: string;
  to: string;
  today: string;
  compact: boolean;
  /** Only the shows the viewer coordinates (acting as coordinator). */
  mine?: boolean;
  /** Shows the viewer highlighted: star filled + neon bar (the list comes sorted, highlighted first). */
  highlightIds?: Set<string>;
  onToggleHighlight?: (show: BoardShow) => void;
  onOpen: (show: BoardShow) => void;
};

/** Every day of the window. */
function windowDays(from: string, to: string): dayjs.Dayjs[] {
  const out: dayjs.Dayjs[] = [];
  let d = dayjs(from);
  const end = dayjs(to);
  while (!d.isAfter(end, "day") && out.length < 400) {
    out.push(d);
    d = d.add(1, "day");
  }
  return out;
}

/**
 * One row per show, one column per day, framed like the dashboard calendar
 * (pink border, pink weekday band, black grid). No "+N more": every show in
 * the window gets its own line. The day columns are drawn once behind all
 * rows and each row only adds its label and its bar, so hundreds of shows
 * stay cheap to render.
 */
export default function ShowTimeline({
  shows,
  from,
  to,
  today,
  compact,
  mine = false,
  highlightIds,
  onToggleHighlight,
  onOpen,
}: Props) {
  const days = useMemo(() => windowDays(from, to), [from, to]);
  const [hovered, setHovered] = useState<string | null>(null);
  const labelWidth = compact ? LABEL_WIDTH_SMALL : LABEL_WIDTH;
  const minColumn = days.length > 60 ? 18 : days.length > 35 ? 26 : 38;
  const columns = `${labelWidth}px repeat(${days.length}, minmax(${minColumn}px, 1fr))`;
  const rowCount = Math.max(1, shows.length);
  const index = (day: string) => dayjs(day).diff(dayjs(from), "day");
  const hover = (id: string) => () => setHovered(id);
  const unhover = (id: string) => () => setHovered((h) => (h === id ? null : h));

  return (
    <Box
      sx={{
        overflowX: "auto",
        borderRadius: "12px",
        border: `1px solid ${THEME.pink}`,
        background: THEME.black,
      }}
    >
      <Box
        aria-label="Shows timeline"
        sx={{
          display: "grid",
          gridTemplateColumns: columns,
          gridTemplateRows: `${HEADER_HEIGHT}px repeat(${rowCount}, ${ROW_HEIGHT}px)`,
          minWidth: labelWidth + days.length * minColumn,
          position: "relative",
        }}
      >
        {/* Pink band: corner + one cell per day (same band as the calendar weekdays) */}
        <Box
          sx={{
            gridColumn: 1,
            gridRow: 1,
            position: "sticky",
            left: 0,
            zIndex: 4,
            background: THEME.pink,
            display: "flex",
            alignItems: "flex-end",
            padding: "0 16px 8px",
          }}
        >
          <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
            {shows.length} show{shows.length === 1 ? "" : "s"}
          </Typography>
        </Box>
        {days.map((d, i) => {
          const key = d.format("YYYY-MM-DD");
          const isToday = key === today;
          const firstOfMonth = i === 0 || d.date() === 1;
          return (
            <Box
              key={`h-${key}`}
              title={d.format("dddd, MMMM D")}
              sx={{
                gridColumn: i + 2,
                gridRow: 1,
                background: THEME.pink,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingBottom: "6px",
                position: "relative",
                borderLeft: d.day() === 0 && i > 0 ? "1px solid rgba(0,0,0,0.25)" : "none",
              }}
            >
              {firstOfMonth ? (
                <Typography
                  sx={{
                    position: "absolute",
                    top: 3,
                    left: 4,
                    fontSize: "12px",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  {d.format("MMM")}
                </Typography>
              ) : null}
              {minColumn >= 26 ? (
                <Typography sx={{ fontSize: "12px", lineHeight: 1.2, opacity: 0.85 }}>
                  {d.format("dd")}
                </Typography>
              ) : null}
              <Typography
                sx={{
                  fontSize: "14px",
                  lineHeight: 1.35,
                  fontWeight: 600,
                  ...(isToday
                    ? { background: THEME.black, color: THEME.pink, borderRadius: "999px", padding: "0 6px" }
                    : {}),
                }}
              >
                {d.date()}
              </Typography>
            </Box>
          );
        })}

        {/* Day columns drawn once behind every row: weekend shade, today tint, separators */}
        {days.map((d, i) => {
          const key = d.format("YYYY-MM-DD");
          const weekend = d.day() === 0 || d.day() === 6;
          const isToday = key === today;
          return (
            <Box
              key={`c-${key}`}
              aria-hidden
              sx={{
                gridColumn: i + 2,
                gridRow: `2 / span ${rowCount}`,
                background: isToday ? THEME.pinkTint : weekend ? THEME.weekend : "transparent",
                borderLeft: `1px solid ${d.day() === 0 ? THEME.pinkSoft : THEME.line}`,
                pointerEvents: "none",
              }}
            />
          );
        })}

        {!shows.length ? (
          <Box sx={{ gridColumn: "1 / -1", gridRow: 2, display: "flex", alignItems: "center", padding: "0 16px" }}>
            <Typography sx={{ opacity: 0.7 }}>
              {mine
                ? "None of the shows you coordinate match these filters in this window."
                : "No shows match these filters in this window."}
            </Typography>
          </Box>
        ) : null}

        {shows.map((show, r) => {
          const row = r + 2;
          const look = barLook(show);
          const startIdx = index(show.startDay ?? from);
          const endIdx = index(show.endDay ?? show.startDay ?? from);
          const start = Math.max(0, startIdx);
          const end = Math.min(days.length - 1, endIdx);
          const clippedLeft = startIdx < 0;
          const clippedRight = endIdx > days.length - 1;
          const isHovered = hovered === show._id;
          const highlighted = !!highlightIds?.has(show._id);
          const subtitle = [clientLabel(show), placeLabel(show)].filter(Boolean).join(" · ");
          const attention = show.attention;
          // Finished shows talk about money (admins): what the client and the crew still owe.
          const money = show.timing === "past" && look.key !== "cancelled" ? show.money : null;
          const moneyLate = !!money?.issues.length;
          const dotColor = attention
            ? SEVERITY_COLORS[attention.severity]
            : moneyLate
              ? SEVERITY_COLORS.critical
              : "transparent";
          // Rough bar width: below ~64px there is no room for text, the tooltip says it all.
          const barWidth = (end - start + 1) * minColumn;
          const tip = `${show.eventName} · ${dateRangeLabel(show)} · ${LIFECYCLE_LABELS[show.lifecycle]} · ${
            money ? (money.finalized ? "closed" : money.reasons.join(", ") || "waiting for payment") : `${show.readiness.percent}% ready`
          }`;
          const radius = 8;

          return (
            <Fragment key={show._id}>
              {/* Full-row hit area (hover + click anywhere on the line) */}
              <Box
                aria-hidden
                onMouseEnter={hover(show._id)}
                onMouseLeave={unhover(show._id)}
                onClick={() => onOpen(show)}
                sx={{
                  gridColumn: "1 / -1",
                  gridRow: row,
                  cursor: "pointer",
                  borderTop: `1px solid ${THEME.pinkSoft}`,
                  background: isHovered ? "rgba(84, 84, 84, 0.45)" : "transparent",
                  zIndex: 1,
                }}
              />
              {/* Sticky label: name, client · city, team photos */}
              <Box
                onMouseEnter={hover(show._id)}
                onMouseLeave={unhover(show._id)}
                onClick={() => onOpen(show)}
                sx={{
                  gridColumn: 1,
                  gridRow: row,
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "0 12px 0 14px",
                  background: isHovered ? THEME.rowHover : THEME.black,
                  borderTop: `1px solid ${THEME.pinkSoft}`,
                  borderRight: `1px solid ${THEME.pinkSoft}`,
                  boxShadow: highlighted ? `inset 4px 0 0 ${NEON.color}` : "none",
                  minWidth: 0,
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: dotColor,
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    title={show.eventName}
                    sx={{
                      fontSize: "15px",
                      fontWeight: 600,
                      lineHeight: 1.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textDecoration: look.strike ? "line-through" : "none",
                      opacity: show.timing === "past" ? 0.7 : 1,
                    }}
                  >
                    {show.eventName}
                  </Typography>
                  {subtitle ? (
                    <Typography
                      sx={{
                        fontSize: "13px",
                        lineHeight: 1.25,
                        opacity: 0.65,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textDecoration: look.key === "cancelled" ? "line-through" : "none",
                      }}
                    >
                      {subtitle}
                    </Typography>
                  ) : null}
                </Box>
                {!compact ? (
                  <CoordinatorAvatars coordinators={show.coordinators} slots={3} size={26} disableTooltips />
                ) : null}
                {onToggleHighlight ? (
                  <HighlightStar on={highlighted} showName={show.eventName} onToggle={() => onToggleHighlight(show)} size={18} />
                ) : null}
              </Box>

              {/* The bar: same look as the calendar bars */}
              {end >= start ? (
                <Tooltip title={tip} arrow placement="top" enterDelay={300}>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label={tip}
                    onMouseEnter={hover(show._id)}
                    onMouseLeave={unhover(show._id)}
                    onClick={() => onOpen(show)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOpen(show);
                      }
                    }}
                    sx={{
                      gridColumn: `${start + 2} / ${end + 3}`,
                      gridRow: row,
                      zIndex: 2,
                      alignSelf: "center",
                      height: 32,
                      ml: clippedLeft ? 0 : "4px",
                      mr: clippedRight ? 0 : "4px",
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: barWidth < 64 ? "0 4px" : "0 8px",
                      cursor: "pointer",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      bgcolor: look.bg,
                      color: look.fg,
                      border: highlighted ? NEON.border : look.border ?? "none",
                      borderRadius: `${clippedLeft ? 0 : radius}px ${clippedRight ? 0 : radius}px ${
                        clippedRight ? 0 : radius
                      }px ${clippedLeft ? 0 : radius}px`,
                      boxShadow: highlighted ? NEON.glow : isHovered ? THEME.glow : "0 1px 2px rgba(0,0,0,0.45)",
                      "&:hover": { filter: "brightness(1.12)" },
                      "&:focus-visible": { outline: `2px solid ${THEME.white}`, outlineOffset: "2px" },
                    }}
                  >
                    {money && barWidth >= 34 ? (
                      money.finalized ? (
                        <CheckCircleRounded sx={{ fontSize: 18, flexShrink: 0, color: THEME.green }} />
                      ) : moneyLate ? (
                        <PaidOutlined sx={{ fontSize: 18, flexShrink: 0, color: "#FF8FA3" }} />
                      ) : null
                    ) : null}
                    {attention && show.lifecycle !== "cancelled" && barWidth >= 34 ? (
                      <WarningAmberRounded
                        sx={{
                          fontSize: 18,
                          flexShrink: 0,
                          color: look.key === "unapproved" ? "#B3122F" : SEVERITY_COLORS[attention.severity],
                          filter: look.key === "running" || look.key === "upcoming" ? "drop-shadow(0 0 1px #000)" : "none",
                        }}
                      />
                    ) : null}
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        color: "inherit",
                        textDecoration: look.strike ? "line-through" : "none",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {barWidth < 64
                        ? ""
                        : look.key === "cancelled"
                          ? "Cancelled"
                          : money
                            ? moneyBarText(money, barWidth)
                            : barWidth >= 120
                            ? `${show.readiness.percent}% · ${
                                show.readiness.crew.total
                                  ? `${show.readiness.crew.filled}/${show.readiness.crew.total} crew`
                                  : "no crew"
                              }`
                            : `${show.readiness.percent}%`}
                    </Typography>
                  </Box>
                </Tooltip>
              ) : null}
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
